import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, FileText, AlertTriangle, Users, Lock, HardDrive, Building, Database, Brain, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AbutmentPlanningAI } from "@/components/iso27001/AbutmentPlanningAI";
import { AIActRiskAssessment } from "@/components/iso27001/AIActRiskAssessment";
import { AIAgentsManager } from "@/components/iso27001/AIAgentsManager";
import { logHetznerShopIntegration } from "@/lib/logHetznerIntegration";

interface ISMSData {
  assetInventory: Asset[];
  riskRegister: Risk[];
  incidents: Incident[];
  bcpData: BCPData;
  accessControl: AccessControl;
  encryption: Encryption;
  suppliers: Supplier[];
  physicalSecurity: PhysicalSecurity;
  aiGovernance: AIGovernance;
}

interface Asset {
  id: string;
  name: string;
  type: string;
  owner: string;
  criticality: 'low' | 'medium' | 'high' | 'critical';
  location: string;
}

interface Risk {
  id: string;
  title: string;
  description: string;
  likelihood: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
  mitigation: string;
  status: 'open' | 'mitigated' | 'accepted';
}

interface Incident {
  id: string;
  date: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  response: string;
  status: 'open' | 'investigating' | 'resolved';
}

interface BCPData {
  rto: string;
  rpo: string;
  backupFrequency: string;
  lastTest: string;
  emergencyContacts: string;
}

interface AccessControl {
  passwordPolicy: string;
  mfaEnabled: boolean;
  reviewFrequency: string;
  privilegedAccess: string;
}

interface Encryption {
  dataAtRest: boolean;
  dataInTransit: boolean;
  algorithm: string;
  keyManagement: string;
}

interface Supplier {
  id: string;
  name: string;
  service: string;
  riskLevel: 'low' | 'medium' | 'high';
  contractReview: string;
  securityAssessment: string;
}

interface PhysicalSecurity {
  accessControl: string;
  surveillance: string;
  environmentalControls: string;
  incidentLog: string;
}

interface AIGovernance {
  aiUsagePolicy: string;
  dataPrivacyAI: string;
  aiRiskAssessment: string;
  humanOversight: string;
  biasMonitoring: string;
  aiModelDocumentation: string;
}

export default function AdminISO27001() {
  const [versionHistory, setVersionHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  
  const [data, setData] = useState<ISMSData>(() => {
    const defaultData: ISMSData = {
      assetInventory: [
        {
          id: '1',
          name: 'Website Server (Lovable Cloud)',
          type: 'Cloud Infrastructure',
          owner: 'IT Administration',
          criticality: 'critical',
          location: 'EU (Frankfurt)'
        },
        {
          id: '2',
          name: 'Supabase Datenbank',
          type: 'Database',
          owner: 'IT Administration',
          criticality: 'critical',
          location: 'EU (Frankfurt)'
        },
        {
          id: '3',
          name: 'Website eap-abutments.com',
          type: 'Web Application',
          owner: 'Marketing/IT',
          criticality: 'high',
          location: 'lovable.app'
        },
        {
          id: '4',
          name: 'E-Mail Server (World4You)',
          type: 'Communication System',
          owner: 'IT Administration',
          criticality: 'high',
          location: 'Österreich'
        },
        {
          id: '5',
          name: 'Kontaktdatenbank',
          type: 'Database',
          owner: 'Vertrieb',
          criticality: 'high',
          location: 'Supabase EU'
        },
        {
          id: '6',
          name: 'Kalendersystem',
          type: 'Business Application',
          owner: 'Verwaltung',
          criticality: 'medium',
          location: 'Supabase EU'
        },
        {
          id: '7',
          name: 'Google Analytics 4',
          type: 'Analytics Tool',
          owner: 'Marketing',
          criticality: 'low',
          location: 'Google Cloud'
        },
        {
          id: '8',
          name: '3D-Modelle (.glb Dateien)',
          type: 'Digital Assets',
          owner: 'Produktmanagement',
          criticality: 'medium',
          location: 'Lovable Storage'
        },
        {
          id: '9',
          name: 'Produktdokumentation (PDFs)',
          type: 'Documents',
          owner: 'Produktmanagement',
          criticality: 'high',
          location: 'Lovable Storage'
        },
        {
          id: '10',
          name: 'Backup-System',
          type: 'Backup Infrastructure',
          owner: 'IT Administration',
          criticality: 'critical',
          location: 'Supabase Automated'
        },
        {
          id: '11',
          name: 'Admin-Panel',
          type: 'Management System',
          owner: 'IT Administration',
          criticality: 'high',
          location: 'Lovable App'
        },
        {
          id: '12',
          name: 'SSL-Zertifikate',
          type: 'Security Infrastructure',
          owner: 'IT Administration',
          criticality: 'critical',
          location: 'Lovable Cloud'
        },
        {
          id: '13',
          name: 'E-Commerce Shop (Hetzner)',
          type: 'Web Application',
          owner: 'E-Commerce/Vertrieb',
          criticality: 'critical',
          location: 'Hetzner Hochsicherheitsserver (Deutschland)'
        },
        {
          id: '14',
          name: 'Shop-Datenbank (Hetzner)',
          type: 'Database',
          owner: 'E-Commerce/IT',
          criticality: 'critical',
          location: 'Hetzner Hochsicherheitsserver (Deutschland)'
        },
        {
          id: '15',
          name: 'Payment-System (Stripe)',
          type: 'Payment Infrastructure',
          owner: 'E-Commerce',
          criticality: 'critical',
          location: 'Stripe (PCI-DSS Level 1)'
        },
        {
          id: '19',
          name: 'B2B Registrierungssystem (Shop)',
          type: 'Access Control System',
          owner: 'E-Commerce/Compliance',
          criticality: 'high',
          location: 'Hetzner Shop'
        },
        {
          id: '20',
          name: 'Berufsgruppen-Verifizierung',
          type: 'Identity Verification',
          owner: 'E-Commerce/Compliance',
          criticality: 'high',
          location: 'Shop Backend'
        },
        {
          id: '16',
          name: 'Source Code Repository',
          type: 'Development Asset',
          owner: 'IT Development',
          criticality: 'critical',
          location: 'GitHub (geplant)'
        },
        {
          id: '17',
          name: 'Video-Assets (Trailer)',
          type: 'Media Files',
          owner: 'Marketing',
          criticality: 'medium',
          location: 'Lovable Storage'
        },
        {
          id: '18',
          name: 'Schriftarten (Lato)',
          type: 'Design Assets',
          owner: 'Design',
          criticality: 'low',
          location: 'Local Hosting'
        }
      ],
      riskRegister: [
        {
          id: 'R001',
          title: 'Datenverlust durch Server-Ausfall',
          description: 'Ausfall der Lovable Cloud oder Supabase-Infrastruktur könnte zu Datenverlust führen.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Automatische tägliche Backups in Supabase, Point-in-Time Recovery aktiviert, regelmäßige Wiederherstellungstests',
          status: 'mitigated'
        },
        {
          id: 'R002',
          title: 'Unbefugter Zugriff auf Admin-Panel',
          description: 'Unautorisierte Personen könnten Zugang zum Admin-Bereich erlangen.',
          likelihood: 'medium',
          impact: 'high',
          mitigation: 'Supabase Auth mit E-Mail-Verifizierung, starke Passwort-Richtlinien, Session-Management, regelmäßige Zugriffsrechte-Reviews',
          status: 'mitigated'
        },
        {
          id: 'R003',
          title: 'DSGVO-Verstoß bei Kontaktdaten',
          description: 'Unzureichender Schutz personenbezogener Daten könnte zu DSGVO-Verstößen führen.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'RLS-Policies in Supabase, Verschlüsselung at rest und in transit, Datenschutzerklärung, Cookie-Consent, dedizierte Datenschutz-E-Mail',
          status: 'mitigated'
        },
        {
          id: 'R004',
          title: 'DDoS-Angriff auf Website',
          description: 'Distributed Denial of Service Angriff könnte Website lahmlegen.',
          likelihood: 'medium',
          impact: 'medium',
          mitigation: 'Lovable Cloud DDoS-Schutz, CDN-Integration, Rate-Limiting in Edge Functions',
          status: 'mitigated'
        },
        {
          id: 'R005',
          title: 'SQL-Injection oder XSS-Angriffe',
          description: 'Code-Injection-Angriffe könnten Datenbank oder Benutzer kompromittieren.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Supabase parametrisierte Queries, Input-Validierung mit Zod, Content Security Policy, sanitized Outputs',
          status: 'mitigated'
        },
        {
          id: 'R006',
          title: 'E-Mail-Spoofing und Phishing',
          description: 'Angreifer könnten gefälschte E-Mails im Namen von EAP versenden.',
          likelihood: 'medium',
          impact: 'medium',
          mitigation: 'SPF, DKIM und DMARC Records konfiguriert, SSL-verschlüsselte E-Mail-Kommunikation über World4You',
          status: 'open'
        },
        {
          id: 'R007',
          title: 'Verlust von geistigem Eigentum',
          description: '3D-Modelle und Produktdokumentation könnten gestohlen oder kopiert werden.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Wasserzeichen auf kritischen Dokumenten, Zugriffskontrollen, Copyright-Hinweise, rechtliche Schutzmaßnahmen',
          status: 'mitigated'
        },
        {
          id: 'R008',
          title: 'Ausfall des E-Mail-Systems',
          description: 'World4You SMTP könnte ausfallen und Kontaktanfragen gehen verloren.',
          likelihood: 'low',
          impact: 'medium',
          mitigation: 'Monitoring der E-Mail-Funktionen, Fallback-Mechanismen in Edge Functions, Benachrichtigung bei fehlgeschlagenen E-Mails',
          status: 'mitigated'
        },
        {
          id: 'R009',
          title: 'Compliance-Verletzung bei internationalen Kunden',
          description: 'Unterschiedliche Datenschutzgesetze in verschiedenen Ländern (GDPR, CCPA, etc.).',
          likelihood: 'medium',
          impact: 'high',
          mitigation: 'EU-Hosting für alle Daten, transparente Datenschutzerklärung in mehreren Sprachen, Cookie-Consent-Management',
          status: 'mitigated'
        },
        {
          id: 'R010',
          title: 'Dependency-Schwachstellen',
          description: 'Sicherheitslücken in npm-Paketen oder Drittanbieter-Bibliotheken.',
          likelihood: 'medium',
          impact: 'medium',
          mitigation: 'Regelmäßige Updates über Lovable, Security Scanning, minimale Anzahl an Dependencies',
          status: 'open'
        },
        {
          id: 'R011',
          title: 'Session-Hijacking',
          description: 'Angreifer könnten Admin-Sessions übernehmen.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Supabase JWT-basierte Authentifizierung, HTTPS-only Cookies, kurze Session-Timeouts, automatisches Logout',
          status: 'mitigated'
        },
        {
          id: 'R012',
          title: 'Datenleck durch unsichere API',
          description: 'Edge Functions könnten sensible Daten exponieren.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'RLS-Policies, API-Authentifizierung, Input-Validierung, keine sensiblen Daten in Logs',
          status: 'mitigated'
        },
        {
          id: 'R013',
          title: 'Ausfall von Drittanbieter-Services',
          description: 'Google Analytics, Resend oder andere Services könnten ausfallen.',
          likelihood: 'medium',
          impact: 'low',
          mitigation: 'Graceful Degradation implementiert, Analytics nicht kritisch für Kernfunktionen, Error Handling',
          status: 'accepted'
        },
        {
          id: 'R014',
          title: 'Insider-Bedrohung',
          description: 'Mitarbeiter mit Admin-Rechten könnten Daten missbrauchen.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Audit-Logs in Supabase, Least-Privilege-Prinzip, regelmäßige Zugriffsrechte-Reviews, Changelog-System',
          status: 'mitigated'
        },
        {
          id: 'R015',
          title: 'Verlust von Geschäftskontinuität',
          description: 'Ausfall kritischer Systeme könnte Geschäftsbetrieb beeinträchtigen.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'BCP/DRP dokumentiert, regelmäßige Backup-Tests, Notfallkontakte definiert, dokumentierte Wiederherstellungsprozesse',
          status: 'open'
        },
        {
          id: 'R016',
          title: 'Shop-Server Ausfall (Hetzner)',
          description: 'Ausfall des E-Commerce Shops könnte Umsatzverlust bedeuten.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Hetzner Hochsicherheitsserver mit SLA, Monitoring, automatische Backups, redundante Systeme',
          status: 'mitigated'
        },
        {
          id: 'R017',
          title: 'Payment-Daten Kompromittierung',
          description: 'Zahlungsdaten im Shop könnten gestohlen werden.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Stripe Payment Provider (PCI-DSS Level 1 zertifiziert), keine Speicherung von Kartendaten auf eigenen Servern, SSL/TLS Verschlüsselung, Tokenisierung durch Stripe',
          status: 'mitigated'
        },
        {
          id: 'R018',
          title: 'Shop-DDoS-Angriff',
          description: 'DDoS-Angriffe könnten Shop-Verfügbarkeit beeinträchtigen.',
          likelihood: 'medium',
          impact: 'high',
          mitigation: 'Hetzner DDoS-Protection, CDN, Rate-Limiting, WAF (Web Application Firewall)',
          status: 'mitigated'
        },
        {
          id: 'R019',
          title: 'Kundendaten-Leak im Shop',
          description: 'DSGVO-Verstoß durch Leak von Kundendaten (Namen, Adressen, Bestellhistorie).',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Verschlüsselte Datenbank, RLS-Policies, regelmäßige Security-Audits, Penetration-Tests',
          status: 'mitigated'
        },
        {
          id: 'R020',
          title: 'Hetzner Infrastruktur-Ausfall',
          description: 'Ausfall der Hetzner-Infrastruktur könnte mehrere Systeme betreffen.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Hetzner 99.9% SLA, geografisch verteilte Backups, Disaster Recovery Plan',
          status: 'open'
        },
        {
          id: 'R021',
          title: 'Unbefugter B2B-Shop Zugang',
          description: 'Nicht-berechtigte Personen (keine Zahnärzte/Zahntechniker/Dentallabore) könnten sich im B2B-Shop registrieren.',
          likelihood: 'medium',
          impact: 'high',
          mitigation: 'Mehrstufiger Registrierungsprozess mit Berufsgruppen-Verifizierung, manuelle Prüfung durch Admin, Nachweis der beruflichen Qualifikation erforderlich (Approbation, Gewerbeschein), E-Mail-Bestätigung mit beruflicher E-Mail-Adresse',
          status: 'mitigated'
        },
        {
          id: 'R022',
          title: 'Fehlerhafte Identitätsprüfung',
          description: 'Unzureichende Verifizierung könnte zu falschen Shop-Freischaltungen führen.',
          likelihood: 'low',
          impact: 'medium',
          mitigation: 'Dokumentenprüfung (Approbationsurkunde, Gewerbeschein), Abgleich mit Berufskammern wo möglich, 4-Augen-Prinzip bei Freischaltung, Audit-Trail aller Verifizierungen',
          status: 'open'
        },
        {
          id: 'R023',
          title: 'DSGVO-Verstoß bei B2B-Kundendaten',
          description: 'Berufsbezogene Daten (Approbationen, Kammerzugehörigkeit) erfordern besonderen Schutz.',
          likelihood: 'low',
          impact: 'high',
          mitigation: 'Verschlüsselte Speicherung von Verifizierungsdokumenten, Zugriffsrechte nur für autorisiertes Personal, automatische Löschung nach Verifizierung (sofern nicht aufbewahrungspflichtig), transparente Datenschutzerklärung für B2B-Registrierung',
          status: 'mitigated'
        },
        {
          id: 'R024',
          title: 'Account-Sharing im B2B-Shop',
          description: 'B2B-Kunden könnten Accounts mit Unbefugten teilen.',
          likelihood: 'medium',
          impact: 'medium',
          mitigation: 'AGB mit Verbot von Account-Sharing, IP-Monitoring für ungewöhnliche Zugriffsmuster, Session-Limits, regelmäßige Re-Verifizierung bei Verdacht',
          status: 'open'
        }
      ],
      incidents: [],
      bcpData: {
        rto: '',
        rpo: '',
        backupFrequency: '',
        lastTest: '',
        emergencyContacts: ''
      },
      accessControl: {
        passwordPolicy: 'Mindestens 12 Zeichen, Groß-/Kleinbuchstaben, Zahlen, Sonderzeichen. B2B-Shop: Zusätzliche Berufsgruppen-Verifizierung erforderlich (Zahnärzte, Zahntechniker, Dentallabore)',
        mfaEnabled: true,
        reviewFrequency: 'Quartalsweise, B2B-Shop-Zugänge: jährliche Re-Verifizierung',
        privilegedAccess: 'Nach Least Privilege Prinzip. B2B-Shop-Registrierung nur nach manueller Admin-Freigabe mit Dokumentenprüfung (Approbation/Gewerbeschein)'
      },
      encryption: {
        dataAtRest: true,
        dataInTransit: true,
        algorithm: 'AES-256',
        keyManagement: 'Cloud HSM'
      },
      suppliers: [
        {
          id: 'S001',
          name: 'Hetzner Online GmbH',
          service: 'Hochsicherheitsserver für E-Commerce Shop',
          riskLevel: 'low',
          contractReview: '2024-12-01',
          securityAssessment: 'ISO 27001 zertifiziert, DDoS-Schutz, 99.9% SLA, Rechenzentrum Deutschland'
        },
        {
          id: 'S002',
          name: 'Lovable Cloud (Supabase)',
          service: 'Website-Hosting, Datenbank, Authentication',
          riskLevel: 'low',
          contractReview: '2024-11-15',
          securityAssessment: 'SOC2 Type II, ISO 27001, EU-Hosting (Frankfurt), automatische Backups'
        },
        {
          id: 'S003',
          name: 'World4You',
          service: 'E-Mail-Services (SMTP)',
          riskLevel: 'low',
          contractReview: '2024-10-20',
          securityAssessment: 'Österreichischer Provider, SSL/TLS, DSGVO-konform'
        },
        {
          id: 'S004',
          name: 'Google LLC',
          service: 'Google Analytics 4',
          riskLevel: 'medium',
          contractReview: '2024-09-10',
          securityAssessment: 'Google AVV abgeschlossen, IP-Anonymisierung aktiviert, Cookie-Consent implementiert'
        },
        {
          id: 'S005',
          name: 'Stripe Inc.',
          service: 'Payment Processing für E-Commerce Shop',
          riskLevel: 'low',
          contractReview: '2024-12-01',
          securityAssessment: 'PCI-DSS Level 1 zertifiziert, SOC 2 Type II, ISO 27001, automatische Betrugserkennung, 3D Secure, keine Kartendaten auf eigenen Servern'
        }
      ],
      physicalSecurity: {
        accessControl: '',
        surveillance: '',
        environmentalControls: '',
        incidentLog: ''
      },
      aiGovernance: {
        aiUsagePolicy: 'KI-Systeme dürfen nur nach Genehmigung und Schulung genutzt werden.',
        dataPrivacyAI: 'Keine personenbezogenen Daten ohne Pseudonymisierung in KI-Modelle einspeisen.',
        aiRiskAssessment: 'Risikobewertung vor Einsatz jedes KI-Tools erforderlich.',
        humanOversight: 'KI-Entscheidungen unterliegen menschlicher Überprüfung.',
        biasMonitoring: 'Regelmäßige Überprüfung auf Bias und Diskriminierung.',
        aiModelDocumentation: 'Vollständige Dokumentation aller verwendeten KI-Modelle und deren Zweck.'
      }
    };

    const saved = localStorage.getItem('iso27001-data');
    if (saved) {
      const savedData = JSON.parse(saved);
      // Only use saved data if arrays are not empty, otherwise use defaults
      return {
        ...defaultData,
        assetInventory: savedData.assetInventory && savedData.assetInventory.length > 0 
          ? savedData.assetInventory 
          : defaultData.assetInventory,
        riskRegister: savedData.riskRegister && savedData.riskRegister.length > 0 
          ? savedData.riskRegister 
          : defaultData.riskRegister,
        incidents: savedData.incidents && savedData.incidents.length > 0 
          ? savedData.incidents 
          : defaultData.incidents,
        suppliers: savedData.suppliers && savedData.suppliers.length > 0 
          ? savedData.suppliers 
          : defaultData.suppliers,
        bcpData: { ...defaultData.bcpData, ...savedData.bcpData },
        accessControl: { ...defaultData.accessControl, ...savedData.accessControl },
        encryption: { ...defaultData.encryption, ...savedData.encryption },
        physicalSecurity: { ...defaultData.physicalSecurity, ...savedData.physicalSecurity },
        aiGovernance: { ...defaultData.aiGovernance, ...savedData.aiGovernance }
      };
    }
    return defaultData;
  });

  useEffect(() => {
    // Log Hetzner Shop integration
    logHetznerShopIntegration();
    
    localStorage.setItem('iso27001-data', JSON.stringify(data));
    
    // Load version history from database
    loadVersionHistory();
  }, [data]);

  const loadVersionHistory = async () => {
    try {
      const { data: versions, error } = await supabase
        .from('iso27001_document_versions')
        .select('*')
        .eq('document_id', 'iso27001-isms')
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) {
        console.error('Error loading version history:', error);
        return;
      }
      
      setVersionHistory(versions || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveData = async () => {
    try {
      // Save to localStorage (existing behavior)
      localStorage.setItem('iso27001-data', JSON.stringify(data));
      
      // Save version to database
      const currentVersion = localStorage.getItem('iso27001-version') || '1.0';
      const versionParts = currentVersion.split('.');
      const newVersion = `${versionParts[0]}.${parseInt(versionParts[1]) + 1}`;
      
      const { error } = await supabase
        .from('iso27001_document_versions')
        .insert([{
          document_id: 'iso27001-isms',
          version: newVersion,
          data: data as any,
          author: 'Admin',
          description: 'ISO 27001 ISMS Daten aktualisiert'
        }]);
      
      if (error) {
        console.error('Error saving to database:', error);
        toast.error('Fehler beim Speichern in der Datenbank');
        return;
      }
      
      localStorage.setItem('iso27001-version', newVersion);
      toast.success(`ISO 27001 Daten gespeichert (Version ${newVersion})`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const exportData = () => {
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `iso27001-export-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    toast.success('Daten exportiert');
  };

  const resetToDefaults = () => {
    localStorage.removeItem('iso27001-data');
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-background to-secondary/5">
      <Navigation />
      <AdminNavigation />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        {showHistory && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Dokumentenverlauf
              </CardTitle>
              <CardDescription>
                Alle gespeicherten Versionen der ISO 27001 Dokumentation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {versionHistory.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    Noch keine Versionen gespeichert
                  </p>
                ) : (
                  versionHistory.map((version) => (
                    <div key={version.id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">Version {version.version}</Badge>
                            <span className="text-sm text-muted-foreground">
                              {new Date(version.created_at).toLocaleString('de-DE')}
                            </span>
                          </div>
                          <p className="text-sm mt-1">{version.description}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Autor: {version.author}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="mb-8">
          <div className="flex flex-col items-start gap-4 mb-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground flex items-start gap-3">
                <Shield className="h-8 w-8 sm:h-10 sm:w-10 shrink-0 text-primary" />
                ISO 27001 Management
              </h1>
              <p className="text-muted-foreground mt-2">
                Information Security Management System (ISMS)
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setShowHistory(!showHistory)} variant="outline">
                {showHistory ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
              </Button>
              <Button onClick={resetToDefaults} variant="destructive">
                Beispieldaten laden
              </Button>
              <Button onClick={saveData} variant="default">
                Speichern
              </Button>
              <Button onClick={exportData} variant="outline">
                Exportieren
              </Button>
            </div>
          </div>
        </div>

        <Tabs defaultValue="assets" className="space-y-6">
          <TabsList className="flex h-auto w-full justify-start overflow-x-auto p-1 [scrollbar-width:thin]">
            <TabsTrigger value="assets" className="shrink-0 text-xs">
              <Database className="h-4 w-4 mr-1" />
              Assets
            </TabsTrigger>
            <TabsTrigger value="risk" className="shrink-0 text-xs">
              <AlertTriangle className="h-4 w-4 mr-1" />
              Risiken
            </TabsTrigger>
            <TabsTrigger value="incidents" className="shrink-0 text-xs">
              <FileText className="h-4 w-4 mr-1" />
              Vorfälle
            </TabsTrigger>
            <TabsTrigger value="bcp" className="shrink-0 text-xs">
              <HardDrive className="h-4 w-4 mr-1" />
              BCP/DRP
            </TabsTrigger>
            <TabsTrigger value="access" className="shrink-0 text-xs">
              <Users className="h-4 w-4 mr-1" />
              Zugriff
            </TabsTrigger>
            <TabsTrigger value="encryption" className="shrink-0 text-xs">
              <Lock className="h-4 w-4 mr-1" />
              Verschlüsselung
            </TabsTrigger>
            <TabsTrigger value="suppliers" className="shrink-0 text-xs">
              <Building className="h-4 w-4 mr-1" />
              Lieferanten
            </TabsTrigger>
            <TabsTrigger value="physical" className="shrink-0 text-xs">
              <Shield className="h-4 w-4 mr-1" />
              Physisch
            </TabsTrigger>
            <TabsTrigger value="ai" className="shrink-0 text-xs">
              <Brain className="h-4 w-4 mr-1" />
              KI & Tools
            </TabsTrigger>
          </TabsList>

          <TabsContent value="assets" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Asset-Verzeichnis</CardTitle>
                <CardDescription>
                  Inventarisierung aller informationstechnischen Assets
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.assetInventory.length === 0 && (
                    <p className="text-muted-foreground text-center py-8">
                      Keine Assets erfasst. Fügen Sie Ihre IT-Assets hinzu.
                    </p>
                  )}
                  <div className="grid gap-4">
                    <div className="hidden md:grid grid-cols-5 gap-2 text-sm font-semibold text-muted-foreground">
                      <div>Name</div>
                      <div>Typ</div>
                      <div>Eigentümer</div>
                      <div>Kritikalität</div>
                      <div>Standort</div>
                    </div>
                    {data.assetInventory.map((asset) => (
                      <div key={asset.id} className="grid grid-cols-1 gap-2 items-center p-3 border rounded-lg md:grid-cols-5">
                        <div className="font-medium"><span className="md:hidden text-xs text-muted-foreground">Name: </span>{asset.name}</div>
                        <div><span className="md:hidden text-xs text-muted-foreground">Typ: </span>{asset.type}</div>
                        <div><span className="md:hidden text-xs text-muted-foreground">Eigentümer: </span>{asset.owner}</div>
                        <div>
                          <span className="md:hidden text-xs text-muted-foreground">Kritikalität: </span>
                          <Badge variant={asset.criticality === 'critical' ? 'destructive' : 'default'}>
                            {asset.criticality}
                          </Badge>
                        </div>
                        <div><span className="md:hidden text-xs text-muted-foreground">Standort: </span>{asset.location}</div>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Beispiel-Assets: Server, Datenbanken, Workstations, Netzwerkgeräte, Software-Lizenzen, etc.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="risk" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Risikoregister</CardTitle>
                <CardDescription>
                  Identifizierte Risiken und Maßnahmen zur Risikominimierung
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.riskRegister.length === 0 && (
                    <p className="text-muted-foreground text-center py-8">
                      Keine Risiken erfasst. Führen Sie eine Risikoanalyse durch.
                    </p>
                  )}
                  {data.riskRegister.map((risk) => (
                    <div key={risk.id} className="p-4 border rounded-lg space-y-2">
                      <div className="flex justify-between items-start">
                        <h4 className="font-semibold">{risk.title}</h4>
                        <Badge variant={risk.status === 'open' ? 'destructive' : 'default'}>
                          {risk.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{risk.description}</p>
                      <div className="flex gap-4 text-sm">
                        <div>
                          <span className="font-medium">Wahrscheinlichkeit:</span>
                          <Badge className="ml-2" variant="outline">{risk.likelihood}</Badge>
                        </div>
                        <div>
                          <span className="font-medium">Auswirkung:</span>
                          <Badge className="ml-2" variant="outline">{risk.impact}</Badge>
                        </div>
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Maßnahmen:</span> {risk.mitigation}
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Beispiel-Risiken: Datenverlust, Cyberangriffe, Systemausfälle, Compliance-Verstöße, etc.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="incidents" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Incident Management</CardTitle>
                <CardDescription>
                  Dokumentation und Behandlung von Sicherheitsvorfällen
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.incidents.length === 0 && (
                    <p className="text-muted-foreground text-center py-8">
                      Keine Vorfälle dokumentiert.
                    </p>
                  )}
                  {data.incidents.map((incident) => (
                    <div key={incident.id} className="p-4 border rounded-lg space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">{incident.type}</h4>
                          <p className="text-sm text-muted-foreground">{incident.date}</p>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant={incident.severity === 'critical' ? 'destructive' : 'default'}>
                            {incident.severity}
                          </Badge>
                          <Badge variant="outline">{incident.status}</Badge>
                        </div>
                      </div>
                      <p className="text-sm">{incident.description}</p>
                      <div className="text-sm">
                        <span className="font-medium">Reaktion:</span> {incident.response}
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Incident Response Plan: Erkennung → Eindämmung → Behebung → Nachbereitung
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bcp" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Business Continuity Plan (BCP) & Disaster Recovery Plan (DRP)</CardTitle>
                <CardDescription>
                  Notfallplanung und Wiederherstellungsverfahren
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Recovery Time Objective (RTO)</Label>
                    <Input
                      value={data.bcpData.rto}
                      onChange={(e) => setData({
                        ...data,
                        bcpData: { ...data.bcpData, rto: e.target.value }
                      })}
                      placeholder="z.B. 4 Stunden"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Recovery Point Objective (RPO)</Label>
                    <Input
                      value={data.bcpData.rpo}
                      onChange={(e) => setData({
                        ...data,
                        bcpData: { ...data.bcpData, rpo: e.target.value }
                      })}
                      placeholder="z.B. 1 Stunde"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Backup-Frequenz</Label>
                    <Input
                      value={data.bcpData.backupFrequency}
                      onChange={(e) => setData({
                        ...data,
                        bcpData: { ...data.bcpData, backupFrequency: e.target.value }
                      })}
                      placeholder="z.B. Täglich, stündlich"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Letzter Test</Label>
                    <Input
                      type="date"
                      value={data.bcpData.lastTest}
                      onChange={(e) => setData({
                        ...data,
                        bcpData: { ...data.bcpData, lastTest: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notfallkontakte</Label>
                    <Textarea
                      value={data.bcpData.emergencyContacts}
                      onChange={(e) => setData({
                        ...data,
                        bcpData: { ...data.bcpData, emergencyContacts: e.target.value }
                      })}
                      placeholder="Namen, Rollen, Telefonnummern"
                      rows={4}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="access" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Zugriffskontroll-Richtlinien</CardTitle>
                <CardDescription>
                  Passwort-Policies, MFA und Berechtigungsverwaltung
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Passwort-Richtlinie</Label>
                  <Textarea
                    value={data.accessControl.passwordPolicy}
                    onChange={(e) => setData({
                      ...data,
                      accessControl: { ...data.accessControl, passwordPolicy: e.target.value }
                    })}
                    rows={3}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="mfa"
                    checked={data.accessControl.mfaEnabled}
                    onCheckedChange={(checked) => setData({
                      ...data,
                      accessControl: { ...data.accessControl, mfaEnabled: checked as boolean }
                    })}
                  />
                  <Label htmlFor="mfa">Multi-Faktor-Authentifizierung (MFA) aktiviert</Label>
                </div>
                <div className="space-y-2">
                  <Label>Review-Frequenz für Berechtigungen</Label>
                  <Input
                    value={data.accessControl.reviewFrequency}
                    onChange={(e) => setData({
                      ...data,
                      accessControl: { ...data.accessControl, reviewFrequency: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Privilegierter Zugriff</Label>
                  <Textarea
                    value={data.accessControl.privilegedAccess}
                    onChange={(e) => setData({
                      ...data,
                      accessControl: { ...data.accessControl, privilegedAccess: e.target.value }
                    })}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="encryption" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Verschlüsselungsrichtlinien</CardTitle>
                <CardDescription>
                  Kryptografische Maßnahmen für Daten at-rest und in-transit
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dataAtRest"
                    checked={data.encryption.dataAtRest}
                    onCheckedChange={(checked) => setData({
                      ...data,
                      encryption: { ...data.encryption, dataAtRest: checked as boolean }
                    })}
                  />
                  <Label htmlFor="dataAtRest">Daten at-rest verschlüsselt</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dataInTransit"
                    checked={data.encryption.dataInTransit}
                    onCheckedChange={(checked) => setData({
                      ...data,
                      encryption: { ...data.encryption, dataInTransit: checked as boolean }
                    })}
                  />
                  <Label htmlFor="dataInTransit">Daten in-transit verschlüsselt (TLS/SSL)</Label>
                </div>
                <div className="space-y-2">
                  <Label>Verschlüsselungs-Algorithmus</Label>
                  <Input
                    value={data.encryption.algorithm}
                    onChange={(e) => setData({
                      ...data,
                      encryption: { ...data.encryption, algorithm: e.target.value }
                    })}
                    placeholder="z.B. AES-256"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Key Management</Label>
                  <Textarea
                    value={data.encryption.keyManagement}
                    onChange={(e) => setData({
                      ...data,
                      encryption: { ...data.encryption, keyManagement: e.target.value }
                    })}
                    placeholder="Beschreibung der Schlüsselverwaltung"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="suppliers" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Supplier Security Management</CardTitle>
                <CardDescription>
                  Bewertung und Überwachung von Lieferanten und Dienstleistern
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {data.suppliers.length === 0 && (
                    <p className="text-muted-foreground text-center py-8">
                      Keine Lieferanten erfasst.
                    </p>
                  )}
                  {data.suppliers.map((supplier) => (
                    <div key={supplier.id} className="p-4 border rounded-lg space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">{supplier.name}</h4>
                          <p className="text-sm text-muted-foreground">{supplier.service}</p>
                        </div>
                        <Badge variant={supplier.riskLevel === 'high' ? 'destructive' : 'default'}>
                          {supplier.riskLevel} risk
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium">Vertragsprüfung:</span> {supplier.contractReview}
                        </div>
                        <div>
                          <span className="font-medium">Security Assessment:</span> {supplier.securityAssessment}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Beispiele: Cloud-Provider, IT-Dienstleister, Software-Lieferanten, Outsourcing-Partner
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="physical" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Physische Sicherheit</CardTitle>
                <CardDescription>
                  Schutz von physischen IT-Assets und Räumlichkeiten
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Zutrittskontrolle</Label>
                  <Textarea
                    value={data.physicalSecurity.accessControl}
                    onChange={(e) => setData({
                      ...data,
                      physicalSecurity: { ...data.physicalSecurity, accessControl: e.target.value }
                    })}
                    placeholder="z.B. Kartenleser, biometrische Scanner, Besucherprotokoll"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Videoüberwachung</Label>
                  <Textarea
                    value={data.physicalSecurity.surveillance}
                    onChange={(e) => setData({
                      ...data,
                      physicalSecurity: { ...data.physicalSecurity, surveillance: e.target.value }
                    })}
                    placeholder="Standorte, Aufbewahrungsfristen"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Umgebungskontrollen</Label>
                  <Textarea
                    value={data.physicalSecurity.environmentalControls}
                    onChange={(e) => setData({
                      ...data,
                      physicalSecurity: { ...data.physicalSecurity, environmentalControls: e.target.value }
                    })}
                    placeholder="z.B. Klimatisierung, Brandschutz, USV, Stromversorgung"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Incident-Protokoll</Label>
                  <Textarea
                    value={data.physicalSecurity.incidentLog}
                    onChange={(e) => setData({
                      ...data,
                      physicalSecurity: { ...data.physicalSecurity, incidentLog: e.target.value }
                    })}
                    placeholder="Dokumentation physischer Sicherheitsvorfälle"
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ai" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-6 w-6 text-primary" />
                  KI-Governance & EU AI Act Compliance
                </CardTitle>
                <CardDescription>
                  Richtlinien für den Einsatz künstlicher Intelligenz gemäß ISO 27001 und EU AI Act
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>KI-Nutzungsrichtlinie</Label>
                  <Textarea
                    value={data.aiGovernance.aiUsagePolicy}
                    onChange={(e) => setData({
                      ...data,
                      aiGovernance: { ...data.aiGovernance, aiUsagePolicy: e.target.value }
                    })}
                    placeholder="Richtlinien für den Einsatz von KI-Systemen"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Datenschutz bei KI-Anwendungen</Label>
                  <Textarea
                    value={data.aiGovernance.dataPrivacyAI}
                    onChange={(e) => setData({
                      ...data,
                      aiGovernance: { ...data.aiGovernance, dataPrivacyAI: e.target.value }
                    })}
                    placeholder="Datenschutzmaßnahmen für KI-Training und -Nutzung"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>KI-Risikobewertung</Label>
                  <Textarea
                    value={data.aiGovernance.aiRiskAssessment}
                    onChange={(e) => setData({
                      ...data,
                      aiGovernance: { ...data.aiGovernance, aiRiskAssessment: e.target.value }
                    })}
                    placeholder="Prozess zur Bewertung von KI-Risiken gemäß EU AI Act"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Menschliche Aufsicht (Human Oversight)</Label>
                  <Textarea
                    value={data.aiGovernance.humanOversight}
                    onChange={(e) => setData({
                      ...data,
                      aiGovernance: { ...data.aiGovernance, humanOversight: e.target.value }
                    })}
                    placeholder="Regelungen zur menschlichen Kontrolle bei KI-Entscheidungen"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bias-Monitoring & Fairness</Label>
                  <Textarea
                    value={data.aiGovernance.biasMonitoring}
                    onChange={(e) => setData({
                      ...data,
                      aiGovernance: { ...data.aiGovernance, biasMonitoring: e.target.value }
                    })}
                    placeholder="Maßnahmen zur Erkennung und Vermeidung von Bias"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>KI-Modell-Dokumentation</Label>
                  <Textarea
                    value={data.aiGovernance.aiModelDocumentation}
                    onChange={(e) => setData({
                      ...data,
                      aiGovernance: { ...data.aiGovernance, aiModelDocumentation: e.target.value }
                    })}
                    placeholder="Dokumentation verwendeter KI-Modelle, Anbieter, Zweck"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <AIActRiskAssessment />

            <AIAgentsManager />

            <AbutmentPlanningAI />

            <Card>
              <CardHeader>
                <CardTitle>Weitere KI-Tools & Automatisierung</CardTitle>
                <CardDescription>
                  Zusätzliche KI-gestützte Werkzeuge für ISO 27001 Compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      KI-gestützte Risikoanalyse
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      Automatische Identifikation und Bewertung von Sicherheitsrisiken basierend auf Asset-Inventory
                    </p>
                    <Button variant="outline" size="sm" disabled>
                      Demnächst verfügbar
                    </Button>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Automatische Compliance-Berichte
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      KI-generierte ISO 27001 Audit-Berichte und Gap-Analysen
                    </p>
                    <Button variant="outline" size="sm" disabled>
                      Demnächst verfügbar
                    </Button>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      Incident Response Assistent
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      KI-gestützte Empfehlungen für die Behandlung von Sicherheitsvorfällen
                    </p>
                    <Button variant="outline" size="sm" disabled>
                      Demnächst verfügbar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
}
